import { bind } from '../../../helpers/decorators';

interface ConstructorParams {
    tracks: MediaStreamTrack[];
    content?: string;
}

export class RecorderAudio {
    public content?: string;

    private audioContext = new AudioContext();
    private destinationNode: MediaStreamAudioDestinationNode;
    private trackToSource = new Map<MediaStreamTrack, MediaStreamAudioSourceNode>();

    constructor({ tracks, content }: ConstructorParams) {
        this.content = content;

        this.destinationNode = this.audioContext.createMediaStreamDestination();

        tracks.forEach((track) => this.addTrack(track));
    }

    @bind
    public getStream() {
        return this.destinationNode.stream;
    }

    @bind
    public getTrack(): MediaStreamTrack {
        const track = this.destinationNode.stream.getTracks()[0];

        if (!track) {
            throw new Error('Recorder track not exists');
        }

        return track;
    }

    @bind
    public async addTrack(track: MediaStreamTrack) {
        if (this.trackToSource.has(track)) {
            return;
        }

        const source = this.audioContext.createMediaStreamSource(new MediaStream([track]));

        source.connect(this.destinationNode);

        this.trackToSource.set(track, source);

        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }
    }

    @bind
    public removeTrack(track: MediaStreamTrack) {
        const source = this.trackToSource.get(track);

        if (!source) {
            return;
        }

        source.disconnect();
        source.mediaStream.removeTrack(track);

        this.trackToSource.delete(track);
    }

    @bind
    public destroy() {
        for (const [, source] of this.trackToSource.entries()) {
            source.disconnect();
        }

        this.trackToSource.clear();
        this.destinationNode.disconnect();
        this.audioContext.close();
    }
}
